import { Box } from '@mantine/core'
import type { Alpha2Code, Alpha3Code } from 'i18n-iso-countries'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useRegions } from '@percent/workplace-giving/common/hooks/useRegions/useRegions'
import { getCountryNameWithOverride } from '@percent/workplace-giving/common/hooks'
import { WorldFlagAvatar } from '@percent/workplace-giving/common/components/WorldFlagAvatar/WorldFlagAvatar'
import countries from '@percent/workplace-giving/i18n/countries'
import { getShortLanguage } from '@percent/workplace-giving/utils/getShortLanguage'
import { FlagAvatar, Select, type SelectOption } from '@percent/lemonade'
import classes from './TargetCountriesPicker.module.scss'

type TargetCountriesPickerProps = Readonly<{
  value: Alpha3Code[]
  onChange: (value: Alpha3Code[]) => void
}>

export function TargetCountriesPicker({ value, onChange }: TargetCountriesPickerProps) {
  const { t, i18n } = useTranslation()
  const { allCountries } = useRegions()

  const shortLanguage = useMemo(() => getShortLanguage(i18n), [i18n])

  const options = useMemo(
    (): SelectOption[] => [
      { value: 'global' as const, label: t('workplace_giving.search.world'), prefix: <WorldFlagAvatar /> },
      ...allCountries
        .map(country => ({
          value: country,
          label: getCountryNameWithOverride(country, shortLanguage),
          prefix: <FlagAvatar code={countries.alpha3ToAlpha2(country) as Alpha2Code} />
        }))
        .sort((a, b) => a.label.localeCompare(b.label, i18n.language))
    ],
    [allCountries, shortLanguage, t, i18n.language]
  )

  const nativeOnChange = useCallback(
    (newValue: SelectOption) => onChange(newValue.value === 'global' ? allCountries : [newValue.value as Alpha3Code]),
    [allCountries, onChange]
  )

  const nativeValue = useMemo(() => {
    if (value.length > 1) {
      return { value: 'global' as const, label: t('workplace_giving.search.world'), prefix: <WorldFlagAvatar /> }
    }

    return {
      value: value[0],
      label: getCountryNameWithOverride(value[0], shortLanguage),
      prefix: <FlagAvatar code={countries.alpha3ToAlpha2(value[0]) as Alpha2Code} />
    }
  }, [value, shortLanguage, t])

  return (
    <Box className={classes.SelectWrapper}>
      <Select
        placeholder={t('workplace_giving.campaigns.countriesPicker.searchPlaceholder')}
        options={options}
        onChange={nativeOnChange}
        defaultValue={nativeValue}
        maxDropdownHeight="21rem"
        minDropdownWidth="268px"
        searchable
        searchInDropDown
        showPrefixForSelectedOption
        borderRadiiSize="large"
      />
    </Box>
  )
}
